import React, { memo, useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import StatusBadge from "../../ui/StatusBadge";
import { spacing, typography } from "../../ui/tokens";
import {
  bandToColor,
  getCellScore,
  getReadableTextColor,
  normalizeMatrixConfig,
  scoreToBand,
} from "../../utils/riskMatrixLogic";

const toLevelMap = (entries) => {
  const map = new Map();
  (entries || []).forEach((entry) => {
    const level = Number(entry?.level);
    if (!Number.isInteger(level)) return;
    map.set(level, {
      label: String(entry?.label || "").trim(),
      definition: String(entry?.definition || "").trim(),
    });
  });
  return map;
};

const getCardPosition = (rect) => {
  if (!rect || typeof window === "undefined") return null;
  const cardWidth = 320;
  const gutter = 12;
  const scrollX = window.scrollX || 0;
  const scrollY = window.scrollY || 0;
  const viewportRight = scrollX + window.innerWidth;
  let left = rect.right + scrollX + gutter;
  const top = rect.top + scrollY;
  if (left + cardWidth > viewportRight - 8) {
    left = rect.left + scrollX - cardWidth - gutter;
  }
  left = Math.max(scrollX + 8, left);
  return { top, left, width: cardWidth };
};

const defaultAriaLabel = ({ likelihood, impact }) =>
  `Select likelihood ${likelihood} and impact ${impact}`;

function RiskMatrixSelectorComponent({
  likelihoodLevels,
  impactLevels,
  selectedLikelihood,
  selectedImpact,
  onSelect,
  impactTheme,
  readOnly = false,
  matrixConfig: rawMatrixConfig,
  disabled = false,
  cellAriaLabelFormatter = defaultAriaLabel,
  compact = false,
  historyPath = [],
}) {
  const matrixConfig = useMemo(() => normalizeMatrixConfig(rawMatrixConfig || null), [rawMatrixConfig]);
  const matrixSize = Number(matrixConfig?.matrix_size) || 5;
  const rowHeaderWidth = compact ? 56 : 104;
  const cellMinWidth = compact ? 44 : 72;
  const cellMinHeight = compact ? 40 : 52;
  const compactRowLabelMaxWidth = compact ? Math.max(rowHeaderWidth - 12, 24) : null;
  const compactImpactLabelMaxWidth = compact ? Math.max(cellMinWidth - 6, 24) : null;
  const levelValues = useMemo(
    () => Array.from({ length: matrixSize }, (_, index) => index + 1),
    [matrixSize]
  );
  const matrixColumns = useMemo(
    () =>
      compact
        ? `${rowHeaderWidth}px repeat(${matrixSize}, ${cellMinWidth}px)`
        : `${rowHeaderWidth}px repeat(${matrixSize}, minmax(${cellMinWidth}px, 1fr))`,
    [compact, rowHeaderWidth, matrixSize, cellMinWidth]
  );
  const rowValues = useMemo(() => [...levelValues].reverse(), [levelValues]);
  const likelihoodByLevel = useMemo(() => toLevelMap(likelihoodLevels), [likelihoodLevels]);
  const impactByLevel = useMemo(() => toLevelMap(impactLevels), [impactLevels]);

  const selectedLikelihoodValue = Number.isInteger(Number(selectedLikelihood)) ? Number(selectedLikelihood) : null;
  const selectedImpactValue = Number.isInteger(Number(selectedImpact)) ? Number(selectedImpact) : null;
  const [hoveredCell, setHoveredCell] = useState(null);
  const matrixSurfaceRef = useRef(null);
  const cellButtonRefs = useRef(new Map());
  const markerId = useId().replace(/:/g, "");
  const [historyOverlay, setHistoryOverlay] = useState({ width: 0, height: 0, points: [] });
  const historyMarkerRadius = 6;
  const historyMarkerPadding = 2;
  const normalizedHistoryPath = useMemo(
    () =>
      (historyPath || [])
        .map((entry, index) => {
          const likelihood = Number(entry?.likelihood);
          const impact = Number(entry?.impact);
          const markerOrder = Number(entry?.markerOrder);
          if (!Number.isInteger(likelihood) || !Number.isInteger(impact)) return null;
          return {
            id: entry?.id || `history-${index}`,
            order: Number.isInteger(markerOrder) && markerOrder > 0 ? markerOrder : index + 1,
            label: String(entry?.label || "").trim(),
            likelihood,
            impact,
          };
        })
        .filter(Boolean),
    [historyPath]
  );

  const recalcHistoryOverlay = useCallback(() => {
    if (!normalizedHistoryPath.length) {
      setHistoryOverlay((prev) => (prev.points.length ? { width: 0, height: 0, points: [] } : prev));
      return;
    }
    const surface = matrixSurfaceRef.current;
    if (!surface) return;
    const surfaceRect = surface.getBoundingClientRect();
    if (!surfaceRect.width || !surfaceRect.height) return;

    const rawPoints = normalizedHistoryPath
      .map((entry) => {
        const key = `cell-${entry.likelihood}-${entry.impact}`;
        const cellNode = cellButtonRefs.current.get(key);
        if (!cellNode) return null;
        const cellRect = cellNode.getBoundingClientRect();
        const cellLeft = cellRect.left - surfaceRect.left;
        const cellTop = cellRect.top - surfaceRect.top;
        return {
          ...entry,
          x: cellLeft + cellRect.width / 2,
          y: cellTop + cellRect.height / 2,
          cellLeft,
          cellTop,
          cellWidth: cellRect.width,
          cellHeight: cellRect.height,
        };
      })
      .filter(Boolean);
    const countsByCell = new Map();
    rawPoints.forEach((point) => {
      const key = `${point.likelihood}-${point.impact}`;
      countsByCell.set(key, (countsByCell.get(key) || 0) + 1);
    });
    const seenByCell = new Map();
    const points = rawPoints.map((point) => {
      const key = `${point.likelihood}-${point.impact}`;
      const total = countsByCell.get(key) || 1;
      const seen = seenByCell.get(key) || 0;
      seenByCell.set(key, seen + 1);
      const minY = point.cellTop + historyMarkerRadius + historyMarkerPadding;
      const maxY = point.cellTop + point.cellHeight - historyMarkerRadius - historyMarkerPadding;
      const yStep = total > 1 ? (maxY - minY) / (total - 1) : 0;
      const y = minY + seen * yStep;
      const x = point.cellLeft + point.cellWidth - historyMarkerRadius - historyMarkerPadding;
      return {
        ...point,
        x,
        y,
      };
    });

    setHistoryOverlay((prev) => {
      const sameSize = prev.width === surfaceRect.width && prev.height === surfaceRect.height;
      const samePoints =
        prev.points.length === points.length &&
        prev.points.every((point, idx) => {
          const nextPoint = points[idx];
          return (
            point.x === nextPoint.x &&
            point.y === nextPoint.y &&
            point.likelihood === nextPoint.likelihood &&
            point.impact === nextPoint.impact
          );
        });
      if (sameSize && samePoints) return prev;
      return { width: surfaceRect.width, height: surfaceRect.height, points };
    });
  }, [normalizedHistoryPath]);

  useEffect(() => {
    recalcHistoryOverlay();
  }, [recalcHistoryOverlay, matrixColumns, rowValues, levelValues]);

  useEffect(() => {
    if (!normalizedHistoryPath.length) return undefined;
    const surface = matrixSurfaceRef.current;
    if (!surface) return undefined;

    let resizeObserver = null;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => recalcHistoryOverlay());
      resizeObserver.observe(surface);
    }
    const handleWindowResize = () => recalcHistoryOverlay();
    if (typeof window !== "undefined") {
      window.addEventListener("resize", handleWindowResize);
    }

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      if (typeof window !== "undefined") {
        window.removeEventListener("resize", handleWindowResize);
      }
    };
  }, [normalizedHistoryPath, recalcHistoryOverlay]);

  const matrixRows = useMemo(
    () =>
      rowValues.map((likelihood) => ({
        likelihood,
        likelihoodLabel: likelihoodByLevel.get(likelihood)?.label || `Level ${likelihood}`,
        cells: levelValues.map((impact) => {
          const score = getCellScore({
            scoringMode: matrixConfig?.scoring_mode,
            cellScores: matrixConfig?.cell_scores,
            lik: likelihood,
            imp: impact,
          });
          const band = scoreToBand({ score, bandThresholds: matrixConfig?.band_thresholds });
          const background = bandToColor({ band, bandColors: matrixConfig?.band_colors });
          return {
            likelihood,
            impact,
            score,
            band,
            background,
            textColor: getReadableTextColor(background),
          };
        }),
      })),
    [rowValues, likelihoodByLevel, levelValues, matrixConfig]
  );

  const hoveredPreview = useMemo(() => {
    if (!hoveredCell) return null;
    const likelihood = Number(hoveredCell?.likelihood);
    const impact = Number(hoveredCell?.impact);
    if (!Number.isInteger(likelihood) || !Number.isInteger(impact)) return null;

    const score = getCellScore({
      scoringMode: matrixConfig?.scoring_mode,
      cellScores: matrixConfig?.cell_scores,
      lik: likelihood,
      imp: impact,
    });
    const band = scoreToBand({ score, bandThresholds: matrixConfig?.band_thresholds });
    const bandColor = bandToColor({ band, bandColors: matrixConfig?.band_colors });
    return {
      likelihood,
      impact,
      score,
      band,
      bandColor,
      likelihoodLabel: likelihoodByLevel.get(likelihood)?.label || "Level",
      likelihoodDefinition: likelihoodByLevel.get(likelihood)?.definition || "Definition unavailable.",
      impactLabel: impactByLevel.get(impact)?.label || "Level",
      impactDefinition: impactByLevel.get(impact)?.definition || "Definition unavailable.",
    };
  }, [hoveredCell, impactByLevel, likelihoodByLevel, matrixConfig]);

  const hoveredCardPosition = useMemo(() => getCardPosition(hoveredCell?.rect), [hoveredCell?.rect]);
  const impactThemeName = String(impactTheme?.name || impactTheme || "General").trim() || "General";
  const interactive = !readOnly && !disabled && typeof onSelect === "function";

  return (
    <div style={matrixRootStyle}>
      <div data-testid="matrix-x-axis-label" style={xAxisLabelStyle}>
        Impact ({impactThemeName})
      </div>
      <div style={matrixAxisWrapStyle}>
        <div data-testid="matrix-y-axis-label" style={yAxisLabelStyle}>
          Likelihood
        </div>
        <div ref={matrixSurfaceRef} style={matrixSurfaceStyle}>
          <div style={{ ...axisHeaderRowStyle, gridTemplateColumns: matrixColumns }}>
            <div style={axisSpacerStyle} />
            {levelValues.map((impact) => (
              <div
                key={`impact-${impact}`}
                data-testid={`matrix-impact-label-${impact}`}
                style={{ ...axisTickCellStyle, minHeight: compact ? 32 : axisTickCellStyle.minHeight }}
              >
                <span style={axisTickCodeStyle}>I{impact}</span>
                <span
                  style={{
                    ...axisTickNameStyle,
                    fontSize: compact ? "9px" : axisTickNameStyle.fontSize,
                    lineHeight: compact ? 1.05 : axisTickNameStyle.lineHeight,
                    whiteSpace: "normal",
                    overflowWrap: "anywhere",
                    wordBreak: "break-word",
                    display: "inline-block",
                    textAlign: "center",
                    width: "100%",
                    maxWidth: compact ? `${compactImpactLabelMaxWidth}px` : "100%",
                  }}
                >
                  {impactByLevel.get(impact)?.label || `Level ${impact}`}
                </span>
              </div>
            ))}
          </div>
          {matrixRows.map((row) => (
            <div key={`row-${row.likelihood}`} style={{ ...matrixRowStyle, gridTemplateColumns: matrixColumns }}>
              <div
                data-testid={`matrix-likelihood-label-${row.likelihood}`}
                style={{ ...axisTickCellStyle, minHeight: cellMinHeight }}
              >
                <span style={axisTickCodeStyle}>L{row.likelihood}</span>
                <span
                  style={{
                    ...axisTickNameStyle,
                    fontSize: compact ? "9px" : axisTickNameStyle.fontSize,
                    lineHeight: compact ? 1.05 : axisTickNameStyle.lineHeight,
                    writingMode: "horizontal-tb",
                    transform: "none",
                    whiteSpace: "normal",
                    overflowWrap: "anywhere",
                    wordBreak: "break-word",
                    display: "inline-block",
                    textAlign: "center",
                    width: "100%",
                    maxWidth: compact ? `${compactRowLabelMaxWidth}px` : "100%",
                  }}
                >
                  {row.likelihoodLabel}
                </span>
              </div>
              {row.cells.map((cell) => {
                const isSelected =
                  selectedLikelihoodValue === cell.likelihood && selectedImpactValue === cell.impact;
                return (
                  <button
                    key={`cell-${cell.likelihood}-${cell.impact}`}
                    ref={(node) => {
                      const key = `cell-${cell.likelihood}-${cell.impact}`;
                      if (node) {
                        cellButtonRefs.current.set(key, node);
                      } else {
                        cellButtonRefs.current.delete(key);
                      }
                    }}
                    type="button"
                    disabled={!interactive}
                    onMouseEnter={(event) =>
                      setHoveredCell({
                        likelihood: cell.likelihood,
                        impact: cell.impact,
                        rect: event.currentTarget.getBoundingClientRect(),
                      })
                    }
                    onMouseLeave={() =>
                      setHoveredCell((current) =>
                        current?.likelihood === cell.likelihood && current?.impact === cell.impact ? null : current
                      )
                    }
                    onFocus={(event) =>
                      setHoveredCell({
                        likelihood: cell.likelihood,
                        impact: cell.impact,
                        rect: event.currentTarget.getBoundingClientRect(),
                      })
                    }
                    onBlur={() =>
                      setHoveredCell((current) =>
                        current?.likelihood === cell.likelihood && current?.impact === cell.impact ? null : current
                      )
                    }
                    onClick={() => onSelect?.({ likelihood: cell.likelihood, impact: cell.impact, score: cell.score })}
                    aria-label={cellAriaLabelFormatter({
                      likelihood: cell.likelihood,
                      impact: cell.impact,
                      score: cell.score,
                    })}
                    style={{
                      ...matrixCellStyle,
                      minHeight: cellMinHeight,
                      background: cell.background,
                      color: cell.textColor,
                      cursor: interactive ? "pointer" : "default",
                      opacity: disabled ? 0.8 : 1,
                      boxShadow: isSelected
                        ? "0 0 0 2px #111827 inset"
                        : "0 0 0 1px rgba(148,163,184,0.6) inset",
                    }}
                  >
                    {cell.score}
                  </button>
                );
              })}
            </div>
          ))}
          {historyOverlay.points.length > 0 && (
            <svg
              data-testid="risk-matrix-history-overlay"
              width={historyOverlay.width}
              height={historyOverlay.height}
              viewBox={`0 0 ${historyOverlay.width} ${historyOverlay.height}`}
              style={historyOverlayStyle}
              aria-hidden="true"
            >
              <defs>
                <marker
                  id={`history-arrow-${markerId}`}
                  markerWidth="8"
                  markerHeight="8"
                  refX="7"
                  refY="4"
                  orient="auto"
                >
                  <path d="M0,0 L8,4 L0,8 z" fill="#0f172a" />
                </marker>
              </defs>
              {historyOverlay.points.length > 1 &&
                historyOverlay.points.slice(0, -1).map((point, index) => {
                  const nextPoint = historyOverlay.points[index + 1];
                  const isFinalSegment = index === historyOverlay.points.length - 2;
                  return (
                    <line
                      key={`history-line-${point.id}-${nextPoint.id}`}
                      data-testid={`risk-matrix-history-line-${index + 1}`}
                      x1={point.x}
                      y1={point.y}
                      x2={nextPoint.x}
                      y2={nextPoint.y}
                      stroke="#0f172a"
                      strokeWidth="2.25"
                      strokeDasharray="5 4"
                      markerEnd={isFinalSegment ? `url(#history-arrow-${markerId})` : undefined}
                    />
                  );
                })}
              {historyOverlay.points.map((point, index) => {
                const isFirst = index === 0;
                const isLast = index === historyOverlay.points.length - 1;
                const fill = isFirst ? "#334155" : isLast ? "#0ea5e9" : "#64748b";
                return (
                  <g
                    key={`history-point-${point.id}`}
                    data-testid={`risk-matrix-history-point-${point.order}`}
                    data-likelihood={point.likelihood}
                    data-impact={point.impact}
                  >
                    <circle
                      cx={point.x}
                      cy={point.y}
                      r={historyMarkerRadius}
                      fill={fill}
                      stroke="#ffffff"
                      strokeWidth="2"
                    />
                    <text
                      x={point.x}
                      y={point.y + 2}
                      textAnchor="middle"
                      fontSize="7"
                      fontWeight="700"
                      fill="#ffffff"
                    >
                      {point.order}
                    </text>
                    <title>
                      {point.label || `Step ${point.order}`}: L{point.likelihood} I{point.impact}
                    </title>
                  </g>
                );
              })}
            </svg>
          )}
        </div>
      </div>

      {hoveredPreview &&
        hoveredCardPosition &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            style={{
              ...hoverCardStyle,
              top: hoveredCardPosition.top,
              left: hoveredCardPosition.left,
              width: hoveredCardPosition.width,
            }}
            role="status"
            aria-live="polite"
          >
            <div style={hoverCardSectionStyle}>
              <div style={hoverCardHeadingStyle}>Likelihood</div>
              <div style={hoverCardValueStyle}>
                L{hoveredPreview.likelihood} - {hoveredPreview.likelihoodLabel}
              </div>
              <div style={hoverCardTextStyle}>{hoveredPreview.likelihoodDefinition}</div>
            </div>
            <div style={hoverCardSectionStyle}>
              <div style={hoverCardHeadingStyle}>Impact ({impactThemeName})</div>
              <div style={hoverCardValueStyle}>
                I{hoveredPreview.impact} - {hoveredPreview.impactLabel}
              </div>
              <div style={hoverCardTextStyle}>{hoveredPreview.impactDefinition}</div>
            </div>
            <div style={hoverCardFooterStyle}>
              <div style={{ display: "grid", gap: 2 }}>
                <span style={hoverCardMetaLabelStyle}>Score</span>
                <strong style={{ fontSize: typography.size.base }}>{hoveredPreview.score ?? "-"}</strong>
              </div>
              <div style={{ display: "grid", gap: 4, justifyItems: "end" }}>
                <span style={hoverCardMetaLabelStyle}>Band</span>
                <div style={{ display: "flex", alignItems: "center", gap: spacing.xs }}>
                  <StatusBadge value={hoveredPreview.band} label={hoveredPreview.band || "Unrated"} />
                  <span
                    style={{
                      width: 12,
                      height: 12,
                      borderRadius: 999,
                      background: hoveredPreview.bandColor,
                      boxShadow: "0 0 0 1px rgba(15,23,42,0.15)",
                    }}
                  />
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}

const RiskMatrixSelector = memo(RiskMatrixSelectorComponent);
export default RiskMatrixSelector;

const matrixRootStyle = {
  display: "grid",
  gap: spacing.xs,
};

const xAxisLabelStyle = {
  textAlign: "center",
  fontSize: typography.size.xs,
  color: "#475569",
  fontWeight: typography.weight.medium,
};

const matrixAxisWrapStyle = {
  display: "grid",
  gridTemplateColumns: "24px 1fr",
  gap: spacing.xs,
  alignItems: "stretch",
};

const yAxisLabelStyle = {
  writingMode: "vertical-rl",
  transform: "rotate(180deg)",
  textAlign: "center",
  fontSize: typography.size.xs,
  color: "#475569",
  fontWeight: typography.weight.medium,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const matrixSurfaceStyle = {
  display: "grid",
  gap: spacing.xs,
  position: "relative",
};

const axisHeaderRowStyle = {
  display: "grid",
  gridTemplateColumns: "120px repeat(5, minmax(72px, 1fr))",
  gap: spacing.xs,
};

const axisSpacerStyle = { minHeight: 24 };

const axisTickCellStyle = {
  minHeight: 38,
  display: "grid",
  alignContent: "center",
  justifyItems: "center",
  textAlign: "center",
  gap: 2,
};

const axisTickCodeStyle = {
  fontSize: typography.size.xs,
  color: "#475569",
  fontWeight: typography.weight.medium,
  lineHeight: 1.1,
};

const axisTickNameStyle = {
  fontSize: "11px",
  color: "#334155",
  fontWeight: typography.weight.medium,
  lineHeight: 1.1,
};

const matrixRowStyle = {
  display: "grid",
  gridTemplateColumns: "120px repeat(5, minmax(72px, 1fr))",
  gap: spacing.xs,
};

const matrixCellStyle = {
  minHeight: 52,
  borderRadius: 8,
  border: "none",
  fontSize: typography.size.sm,
  fontWeight: typography.weight.bold,
  transition: "box-shadow 0.12s ease",
};

const historyOverlayStyle = {
  position: "absolute",
  inset: 0,
  zIndex: 4,
  pointerEvents: "none",
};

const hoverCardStyle = {
  position: "absolute",
  zIndex: 4001,
  borderRadius: 10,
  border: "1px solid rgba(148,163,184,0.35)",
  background: "#ffffff",
  boxShadow: "0 14px 32px rgba(15,23,42,0.16)",
  padding: spacing.sm,
  display: "grid",
  gap: spacing.sm,
  pointerEvents: "none",
};

const hoverCardSectionStyle = {
  display: "grid",
  gap: spacing.xs / 2,
};

const hoverCardHeadingStyle = {
  fontSize: typography.size.xs,
  color: "#475569",
  fontWeight: typography.weight.medium,
};

const hoverCardValueStyle = {
  fontSize: typography.size.sm,
  color: "#111827",
  fontWeight: typography.weight.medium,
};

const hoverCardTextStyle = {
  fontSize: typography.size.xs,
  color: "#4b5563",
  lineHeight: 1.4,
};

const hoverCardFooterStyle = {
  borderTop: "1px solid #e2e8f0",
  paddingTop: spacing.sm,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
};

const hoverCardMetaLabelStyle = {
  fontSize: typography.size.xs,
  color: "#6b7280",
};
